import React, { useState } from 'react';
import styled from 'styled-components';
import { Card, Button, Badge, Input } from '../../styles/components';
import { OrganizationStats, useSetOrganizationSystemCertificateMutation, useUpdateOrganizationMutation } from '../../store/api/organizationsApi';
import { useNotifications } from '../../hooks/useNotifications';
import {
  BuildingOfficeIcon,
  CalendarIcon,
  UsersIcon,
  ShieldCheckIcon,
  ArrowLeftIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon as XIcon
} from '@heroicons/react/24/outline';

const DetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xl};
`;

const DetailsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${props => props.theme.spacing.md};
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
`;

const BackButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  background-color: ${props => props.theme.colors.gray[100]};
  color: ${props => props.theme.colors.gray[600]};
  
  &:hover {
    background-color: ${props => props.theme.colors.gray[200]};
    color: ${props => props.theme.colors.gray[700]};
  }
  
  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const OrganizationIcon = styled.div`
  width: 3rem;
  height: 3rem;
  border-radius: ${props => props.theme.borders.radius.lg};
  background-color: ${props => props.theme.colors.primary[100]};
  color: ${props => props.theme.colors.primary[600]};
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 1.5rem;
    height: 1.5rem;
  }
`;

const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

const OrganizationName = styled.h1`
  font-size: ${props => props.theme.fontSizes.xl};
  font-weight: 600;
  color: ${props => props.theme.colors.gray[900]};
  margin: 0;
`;

const EditableNameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const EditButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing.xs};
  background: none;
  border: none;
  color: ${props => props.theme.colors.gray[400]};
  cursor: pointer;
  border-radius: ${props => props.theme.borders.radius.sm};
  transition: all 0.2s ease;

  &:hover {
    color: ${props => props.theme.colors.primary[600]};
    background-color: ${props => props.theme.colors.gray[100]};
  }

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const NameEditInput = styled(Input)`
  font-size: ${props => props.theme.fontSizes.xl};
  font-weight: 600;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  margin: 0;
`;

const EditActions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.xs};
`;

const EditActionButton = styled.button<{ variant?: 'save' | 'cancel' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing.xs};
  border-radius: ${props => props.theme.borders.radius.sm};
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.variant === 'save' ? `
    background-color: ${props.theme.colors.primary[600]};
    color: white;

    &:hover {
      background-color: ${props.theme.colors.primary[700]};
    }
  ` : `
    background-color: ${props.theme.colors.gray[200]};
    color: ${props.theme.colors.gray[600]};

    &:hover {
      background-color: ${props.theme.colors.gray[300]};
    }
  `}

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const OrganizationSubtitle = styled.p`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.gray[500]};
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
`;

const ActionButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  
  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: ${props => props.theme.spacing.xl};
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xl};
`;

const InfoCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const InfoTitle = styled.h2`
  font-size: ${props => props.theme.fontSizes.lg};
  font-weight: 600;
  color: ${props => props.theme.colors.gray[900]};
  margin: 0;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${props => props.theme.spacing.md};
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

const InfoLabel = styled.span`
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: 500;
  color: ${props => props.theme.colors.gray[500]};
`;

const InfoValue = styled.span`
  font-size: ${props => props.theme.fontSizes.base};
  color: ${props => props.theme.colors.gray[900]};
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const StatsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const StatCard = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  text-align: center;
`;

const StatIcon = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background-color: ${props => props.theme.colors.primary[100]};
  color: ${props => props.theme.colors.primary[600]};
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const StatValue = styled.div`
  font-size: ${props => props.theme.fontSizes['2xl']};
  font-weight: 700;
  color: ${props => props.theme.colors.gray[900]};
`;

const StatLabel = styled.div`
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: 500;
  color: ${props => props.theme.colors.gray[500]};
`;

const StatSubtext = styled.div`
  font-size: ${props => props.theme.fontSizes.xs};
  color: ${props => props.theme.colors.gray[400]};
`;

const CertificateCard = styled.div`
  padding: ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.gray[50]};
  border-radius: ${props => props.theme.borders.radius.md};
  border: 1px solid ${props => props.theme.colors.gray[200]};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const CertificateHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${props => props.theme.spacing.md};
`;

const CertificateInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

const CertificateTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  color: ${props => props.theme.colors.gray[900]};

  svg {
    width: 1rem;
    height: 1rem;
    color: ${props => props.theme.colors.primary[600]};
  }
`;

const CertificateDescription = styled.div`
  font-size: ${props => props.theme.fontSizes.xs};
  color: ${props => props.theme.colors.gray[600]};
  line-height: 1.4;
`;

const CertificateActions = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${props => props.theme.spacing.sm};
`;

const CertActionButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  font-size: ${props => props.theme.fontSizes.sm};
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  white-space: nowrap;

  svg {
    width: 0.875rem;
    height: 0.875rem;
  }
`;

const WarningMessage = styled.div`
  font-size: ${props => props.theme.fontSizes.xs};
  color: ${props => props.theme.colors.warning[700]};
  background-color: ${props => props.theme.colors.warning[50]};
  padding: ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borders.radius.sm};
  border: 1px solid ${props => props.theme.colors.warning[200]};
  display: flex;
  align-items: flex-start;
  gap: ${props => props.theme.spacing.xs};
  line-height: 1.4;

  svg {
    width: 0.875rem;
    height: 0.875rem;
    flex-shrink: 0;
    margin-top: 0.125rem;
  }
`;

const ErrorMessage = styled(WarningMessage)`
  color: ${props => props.theme.colors.error[700]};
  background-color: ${props => props.theme.colors.error[50]};
  border-color: ${props => props.theme.colors.error[200]};
`;

const SuccessMessage = styled(WarningMessage)`
  color: ${props => props.theme.colors.success[700]};
  background-color: ${props => props.theme.colors.success[50]};
  border-color: ${props => props.theme.colors.success[200]};
`;

interface OrganizationDetailsProps {
  organization: OrganizationStats;
  onBack: () => void;
  onDelete?: () => void;
}

export function OrganizationDetails({ organization, onBack, onDelete }: OrganizationDetailsProps) {
  const { notify } = useNotifications();
  const [setSystemCertificate, { isLoading: isTogglingCert, error: certError }] = useSetOrganizationSystemCertificateMutation();
  const [updateOrganization, { isLoading: isUpdating }] = useUpdateOrganizationMutation();
  const [localError, setLocalError] = useState<string | null>(null);
  const [pendingToggle, setPendingToggle] = useState<boolean | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(organization.name);

  const hasBusinessSystem = Boolean(organization.businessSystemId);
  const canToggle = hasBusinessSystem;

  const handleStartEditName = () => {
    setEditedName(organization.name);
    setIsEditingName(true);
  };

  const handleCancelEditName = () => {
    setEditedName(organization.name);
    setIsEditingName(false);
  };

  const handleSaveName = async () => {
    if (!editedName.trim()) {
      notify.error('Validation error', 'Organization name cannot be empty');
      return;
    }

    if (editedName === organization.name) {
      setIsEditingName(false);
      return;
    }

    try {
      await updateOrganization({
        id: organization.id,
        organizationId: organization.organizationId,
        name: editedName.trim(),
        businessSystemId: organization.businessSystemId
      }).unwrap();

      notify.success('Organization updated', 'Organization name has been updated successfully.');
      setIsEditingName(false);
    } catch (error: any) {
      const errorMessage = error?.data?.errors?.[0] || 'Failed to update organization name';
      notify.error('Update failed', errorMessage);
    }
  };

  const handleToggleSystemCertificate = (newValue: boolean) => {
    setLocalError(null);

    if (newValue && !hasBusinessSystem) {
      setLocalError('Organization must have a business system to use system certificates');
      return;
    }

    setPendingToggle(newValue);
  };

  const handleConfirmToggle = async () => {
    if (pendingToggle === null) return;

    try {
      await setSystemCertificate({
        id: organization.id,
        useSystemCertificate: pendingToggle
      }).unwrap();

      const action = pendingToggle ? 'enabled' : 'disabled';
      notify.success('Certificate settings updated', `System certificate has been ${action} successfully.`);
    } catch (error: any) {
      const errorMessage = error?.data?.errors?.[0] || 'Failed to update system certificate setting';
      setLocalError(errorMessage);
      notify.error('Update failed', errorMessage);
    } finally {
      setPendingToggle(null);
    }
  };

  const handleCancelToggle = () => {
    setPendingToggle(null);
  };

  const displayError = localError || (certError as any)?.data?.errors?.[0];

  return (
    <DetailsContainer>
      <DetailsHeader>
        <HeaderLeft>
          <BackButton onClick={onBack}>
            <ArrowLeftIcon />
            Back
          </BackButton>
          <OrganizationIcon>
            <BuildingOfficeIcon />
          </OrganizationIcon>
          <HeaderInfo>
            {isEditingName ? (
              <EditableNameContainer>
                <NameEditInput
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveName();
                    if (e.key === 'Escape') handleCancelEditName();
                  }}
                  autoFocus
                  disabled={isUpdating}
                />
                <EditActions>
                  <EditActionButton
                    variant="save"
                    onClick={handleSaveName}
                    disabled={isUpdating}
                    title="Save"
                  >
                    <CheckIcon />
                  </EditActionButton>
                  <EditActionButton
                    variant="cancel"
                    onClick={handleCancelEditName}
                    disabled={isUpdating}
                    title="Cancel"
                  >
                    <XIcon />
                  </EditActionButton>
                </EditActions>
              </EditableNameContainer>
            ) : (
              <EditableNameContainer>
                <OrganizationName>{organization.name}</OrganizationName>
                <EditButton onClick={handleStartEditName} title="Edit organization name">
                  <PencilIcon />
                </EditButton>
              </EditableNameContainer>
            )}
            <OrganizationSubtitle>
              ID: {organization.organizationId}
              {organization.businessSystemName && ` • ${organization.businessSystemName}`}
            </OrganizationSubtitle>
          </HeaderInfo>
        </HeaderLeft>
        <HeaderActions>
          {onDelete && (
            <ActionButton variant="secondary" onClick={onDelete}>
              <TrashIcon />
              Delete
            </ActionButton>
          )}
        </HeaderActions>
      </DetailsHeader>

      <DetailsGrid>
        <MainContent>
          <InfoCard>
            <InfoSection>
              <InfoTitle>Organization Information</InfoTitle>
              <InfoGrid>
                <InfoItem>
                  <InfoLabel>Name</InfoLabel>
                  <InfoValue>{organization.name}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Organization ID</InfoLabel>
                  <InfoValue>{organization.organizationId}</InfoValue>
                </InfoItem>
                {organization.businessSystemName && (
                  <InfoItem>
                    <InfoLabel>Business System</InfoLabel>
                    <InfoValue>{organization.businessSystemName}</InfoValue>
                  </InfoItem>
                )}
                <InfoItem>
                  <InfoLabel>Created</InfoLabel>
                  <InfoValue>
                    {new Date(organization.created).toLocaleDateString()}
                  </InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Status</InfoLabel>
                  <InfoValue>
                    <Badge variant={organization.isDeleted ? 'error' : 'success'}>
                      {organization.isDeleted ? 'Deleted' : 'Active'}
                    </Badge>
                  </InfoValue>
                </InfoItem>
              </InfoGrid>
            </InfoSection>

            <InfoSection>
              <InfoTitle>Certificate Configuration</InfoTitle>
              <CertificateCard>
                <CertificateHeader>
                  <CertificateInfo>
                    <CertificateTitle>
                      <ShieldCheckIcon />
                      System Certificate
                      <Badge variant={organization.useSystemCertificate ? 'success' : 'info'}>
                        {organization.useSystemCertificate ? 'Active' : 'Inactive'}
                      </Badge>
                    </CertificateTitle>
                    <CertificateDescription>
                      {organization.useSystemCertificate
                        ? `This organization uses the shared certificate for ${organization.businessSystemName}. Individual client certificates are not required.`
                        : hasBusinessSystem
                        ? `Enable to use the shared ${organization.businessSystemName} certificate instead of individual client certificates.`
                        : 'Assign a business system to this organization to enable shared certificate usage.'}
                    </CertificateDescription>
                  </CertificateInfo>
                  <CertificateActions>
                    {organization.useSystemCertificate ? (
                      <CertActionButton
                        variant="secondary"
                        size="sm"
                        onClick={() => handleToggleSystemCertificate(false)}
                        disabled={isTogglingCert}
                      >
                        {isTogglingCert ? 'Disabling...' : 'Disable'}
                      </CertActionButton>
                    ) : (
                      <CertActionButton
                        size="sm"
                        onClick={() => handleToggleSystemCertificate(true)}
                        disabled={!canToggle || isTogglingCert}
                      >
                        {isTogglingCert ? 'Enabling...' : 'Enable System Cert'}
                      </CertActionButton>
                    )}
                  </CertificateActions>
                </CertificateHeader>

                {!hasBusinessSystem && (
                  <WarningMessage>
                    <ExclamationTriangleIcon />
                    <span>A business system must be assigned before you can enable system certificates.</span>
                  </WarningMessage>
                )}

                {pendingToggle !== null && (
                  <WarningMessage>
                    <ExclamationTriangleIcon />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', flex: 1 }}>
                      <span>
                        {pendingToggle
                          ? 'Do you want to enable system certificate for this organization? If confirmed, all clients in the organization will use the system certificate'
                          : 'Do you want to disable system certificate? If confirmed, remember to configure a certificate individually for each client'}
                      </span>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <CertActionButton size="sm" onClick={handleConfirmToggle} disabled={isTogglingCert}>
                          {isTogglingCert ? 'Saving...' : 'Confirm'}
                        </CertActionButton>
                        <CertActionButton variant="secondary" size="sm" onClick={handleCancelToggle} disabled={isTogglingCert}>
                          Cancel
                        </CertActionButton>
                      </div>
                    </div>
                  </WarningMessage>
                )}

                {displayError && (
                  <ErrorMessage>
                    <ExclamationTriangleIcon />
                    <span>{displayError}</span>
                  </ErrorMessage>
                )}
              </CertificateCard>
            </InfoSection>
          </InfoCard>
        </MainContent>

        <Sidebar>
          <StatsGrid>
            <StatCard>
              <StatIcon>
                <UsersIcon />
              </StatIcon>
              <StatValue>{organization.totalApiClients}</StatValue>
              <StatLabel>API Clients</StatLabel>
              <StatSubtext>{organization.activeApiClients} active</StatSubtext>
            </StatCard>

            <StatCard>
              <StatIcon>
                <ShieldCheckIcon />
              </StatIcon>
              <StatValue>{organization.totalManagementClients}</StatValue>
              <StatLabel>Management Users</StatLabel>
            </StatCard>
          </StatsGrid>
        </Sidebar>
      </DetailsGrid>
    </DetailsContainer>
  );
}